import React, { useState, useRef } from 'react';
import cx from 'classnames';

import { useCaretPosition } from '~hooks/common';
import Loader from '~components/loader';
import useMaskedInput from './use-masked-input';
import withControlled from '../form/with-controlled';

import './style.scss';

const ENTER_KEY = 13;

let inputIdCounter = 0;
const getNextInputId = () => {
  inputIdCounter += 1;
  return inputIdCounter;
};

export const Input = ({
  label,
  name,
  className,
  value,
  disabled = false,
  submitOnEnter = true,
  inputRef = useRef(),
  icon,
  leftIcon,
  placeholder,
  errors,
  isInvalid,
  mask,
  isValidating,
  onKeyUp = () => {},
  onChange = () => {},
  onBlur = () => {},
  ...props
}) => {
  const [isActive, setIsActive] = useState(false);
  const [errorId] = useState(() => `input-error-${getNextInputId()}`);

  const inputMaskOnChange = mask
    ? useMaskedInput({
        mask,
        value,
        input: inputRef,
        onChange,
      })
    : null;

  // Track the caret position with the hook
  const { updateCaret } = useCaretPosition(inputRef, isActive);

  const handleKeyDown = (ev) => {
    if (!submitOnEnter && ev.keyCode === ENTER_KEY) {
      inputRef.current.blur();
      ev.preventDefault();
      onKeyUp(ev);
    }
  };

  const handleChange = (ev) => {
    if (mask) {
      setIsActive(true);
      inputMaskOnChange(ev);
      updateCaret();
      return;
    }

    onChange(ev);
  };

  const handleBlur = (ev) => {
    setIsActive(false);
    onBlur(ev);
  };

  return (
    <label
      className={cx('c-form__input', {
        'c-form__input--invalid': isInvalid && !disabled,
        [className]: !!className,
      })}
    >
      <div className="c-form__placeholder">{placeholder}</div>
      {label && <div className="c-form__label">{label}</div>}
      {leftIcon && <div className="c-input__icon c-input__icon--left">{leftIcon}</div>}
      <input
        className={cx('c-input', {
          'c-input--with-icon': !!icon,
          'c-input--with-left-icon': !!leftIcon,
        })}
        name={name}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onKeyUp={onKeyUp}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={inputRef}
        aria-invalid={isInvalid && !disabled ? 'true' : undefined}
        aria-describedby={errors && !disabled ? errorId : undefined}
        {...props}
      />
      {errors && !disabled && (
        <div id={errorId} className="c-input__error" role="alert">
          {errors.message}
        </div>
      )}
      {icon && <div className="c-input__icon c-input__icon--right">{icon}</div>}
      {isValidating && <Loader className="c-input__validation-loader" />}
    </label>
  );
};

export const InputControlled = withControlled(Input);
